import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import React from 'react';
import { TextInput } from 'react-hook-form-mantine';
import { EditableInfoContainer } from '@dealstack/frontend/shared/components/EditableInfoContainer/EditableInfoContainer.tsx';
import { FormProvider } from 'react-hook-form';
import { EditableInfoField } from '@dealstack/frontend/shared/components/EditableInfoField/EditableInfoField.tsx';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';

import { Flex, MultiSelect } from '@mantine/core';
import { EntityTags } from '@dealstack/frontend/app/pages/Project/Cap/common/EntityTags/EntityTags.tsx';
import { usePersonClassifierSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/LegalPersonDetails/hook/usePersonClassifierSection.ts';
import { LegalPersonTags } from '@dealstack/shared/lib/generated';
import { TAGS_OPTIONS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/utils.ts';
import { EditableInfoContainerGrid } from '@dealstack/frontend/shared/components/EditableInfoContainer/styled.tsx';
import { FieldInfo } from './FieldInfo';
import { CLASSIFIERS_FORM_FIELDS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/LegalPersonDetails/hook/types.ts';
import { TEST_ELEMENT_IDS, makeComplexDataTestId } from '@dealstack/shared/tests/testElementIds';
import { makeOptionWithTestId } from '@dealstack/frontend/shared/components/Select/withOptionTestId';

const ClassifierTagOption = makeOptionWithTestId(TEST_ELEMENT_IDS.CAP_CLASSIFIERS_TAG_OPTION);

interface Props {
	hasEditorPermission: boolean;
}

export const PersonClassifierSection = ({ hasEditorPermission }: Props) => {
	const tCapManage = useTranslationCapManage();
	const translatedTagsOptions = TAGS_OPTIONS.map((option) => ({
		...option,
		label: tCapManage(option.label),
	}));
	const [isEditModeEnabled, setIsEditModeEnabled] = React.useState(false);

	const isFieldDisabled = (fieldName: CLASSIFIERS_FORM_FIELDS) =>
		isLoading && classifiersForm.getFieldState(fieldName).isDirty;

	const { classifiersForm, onFormSubmit, isLoading, selectedEntityData } =
		usePersonClassifierSection();

	return (
		<EditableInfoContainer
			title={tCapManage('basic_details.legal_person_form.classifiers')}
			isEdit={isEditModeEnabled}
			setIsEdit={setIsEditModeEnabled}
			disableButtons={isLoading}
			onUpdate={classifiersForm.handleSubmit(onFormSubmit)}
			onCancel={() => setIsEditModeEnabled(false)}
			hideRemove={true}
			hasEditorPermission={hasEditorPermission}
			editBtnDataTestId={makeComplexDataTestId(
				TEST_ELEMENT_IDS.EDITABLE_INFO_CONTAINER_EDIT_BUTTON,
				'CLASSIFIERS'
			)}
			updateBtnDataTestId={makeComplexDataTestId(
				TEST_ELEMENT_IDS.EDITABLE_INFO_CONTAINER_UPDATE_BUTTON,
				'CLASSIFIERS'
			)}
		>
			<FormProvider {...classifiersForm}>
				<form
					onSubmit={classifiersForm.handleSubmit(onFormSubmit)}
					style={{ width: '100%' }}
				>
					<EditableInfoContainerGrid $gridColumns={2}>
						<EditableInfoField
							editMode={isEditModeEnabled}
							label={tCapManage('basic_details.legal_person_form.tags_label')}
							customValueElement={
								<Flex mt="0.25rem">
									{selectedEntityData?.tags?.length ? (
										<EntityTags
											tags={selectedEntityData.tags}
											dropdownPosition="bottom-start"
											testIdBase={TEST_ELEMENT_IDS.CAP_CLASSIFIERS_TAG_BADGE}
										/>
									) : (
										LONG_DASH
									)}
								</Flex>
							}
							formInput={
								<MultiSelect
									data={translatedTagsOptions}
									name={CLASSIFIERS_FORM_FIELDS.tags}
									value={classifiersForm.watch(CLASSIFIERS_FORM_FIELDS.tags)}
									onChange={(value: LegalPersonTags[]) => {
										classifiersForm.setValue(
											CLASSIFIERS_FORM_FIELDS.tags,
											value,
											{
												shouldDirty: true,
											}
										);
									}}
									placeholder={tCapManage(
										'basic_details.legal_person_form.tags_placeholder'
									)}
									rightSection={<Icon variant="caret-down" size="lg" />}
									styles={{
										rightSection: { pointerEvents: 'none' },
									}}
									inputWrapperOrder={['label', 'input', 'description']}
									description={
										<FieldInfo
											text={tCapManage(
												'basic_details.legal_person_form.tags_info'
											)}
										/>
									}
									size="md"
									clearable
									multiple
									disabled={
										!hasEditorPermission ||
										isFieldDisabled(CLASSIFIERS_FORM_FIELDS.tags)
									}
									renderOption={ClassifierTagOption}
									data-testid={TEST_ELEMENT_IDS.CAP_CLASSIFIERS_TAGS_MULTISELECT}
								/>
							}
						/>
						<EditableInfoField
							editMode={isEditModeEnabled}
							label={tCapManage(
								'basic_details.legal_person_form.friendly_name_label'
							)}
							data={selectedEntityData?.displayName}
							formInput={
								<TextInput
									name={CLASSIFIERS_FORM_FIELDS.display_name}
									control={classifiersForm.control}
									size="md"
									inputWrapperOrder={['label', 'input', 'description']}
									placeholder={tCapManage(
										'basic_details.legal_person_form.friendly_name_placeholder'
									)}
									description={
										<FieldInfo
											text={tCapManage(
												'basic_details.legal_person_form.friendly_name_info'
											)}
										/>
									}
									disabled={
										!hasEditorPermission ||
										isFieldDisabled(CLASSIFIERS_FORM_FIELDS.display_name)
									}
								/>
							}
						/>
					</EditableInfoContainerGrid>
				</form>
			</FormProvider>
		</EditableInfoContainer>
	);
};
